import { Link } from 'react-router-dom'
import { ArrowRight, Images } from 'lucide-react'
import SectionHeading from '@/components/feature/SectionHeading'
import { gallery } from '@/data/gallery'
import { useLanguage } from '@/localization'

/** Curated bento strip of real Uliunai screenshots. One feature tile + a tidy
 *  cluster around it, linking to the full /gallery. Sits right after the
 *  servers pitch so visitors see the actual world early. */

// Hand-picked spread (feature first, then variety) — keeps the strip tight.
const picks = [0, 19, 9, 6, 14, 11, 2] as const

const tileClass: Record<number, string> = {
  0: 'col-span-2 row-span-2', // hero feature
  1: 'col-span-2 row-span-1', // wide
}

export default function GalleryTeaser() {
  const shots = picks.map((i) => gallery[i])
  const { t } = useLanguage()

  return (
    <section className="mx-auto w-full max-w-6xl px-4 py-16 sm:py-20">
      <SectionHeading
        eyebrow="From the world"
        title="Built by the community"
        subtitle="Villages, boss fights and views from across the Uliunai survival map — shaders on."
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 auto-rows-[140px] sm:auto-rows-[170px] [grid-auto-flow:dense]">
        {shots.map((item, i) => (
          <Link
            key={i}
            to="/gallery"
            className={`group relative overflow-hidden rounded-2xl border border-stone-light/50 bg-slate-deep/40 shadow-block ${tileClass[i] ?? ''}`}
          >
            <img
              src={item.src}
              alt={t(item.caption)}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.08]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-deep via-slate-deep/10 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            <p className="absolute inset-x-0 bottom-0 translate-y-1 p-3 text-sm font-bold leading-tight text-white opacity-0 drop-shadow transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
              {t(item.caption)}
            </p>
          </Link>
        ))}
      </div>

      <div className="mt-8 flex justify-center">
        <Link
          to="/gallery"
          className="inline-flex items-center gap-2 rounded-full bg-white/90 px-5 py-2.5 text-sm font-bold text-grass-deep shadow-block transition-colors hover:bg-white"
        >
          <Images size={16} /> {t('View the full gallery')} <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  )
}
